import { Controller } from "stimulus";
import debounce from "lodash/debounce";

export default class extends Controller {
  static values = {
    url: String,
    selectedCountTexts: Object,
    individualStartTooltip: String,
  };

  static targets = [
    "table",
    "startDateInput",
    "endDateInput",
    "searchInput",
    "statusSelect",
    "emptyState",
    "recordsCount",
    "selectedCount",
  ];

  connect() {
    this.abortController = null;
    this.state = {
      programIds: new Set(),
      startDate: "",
      endDate: "",
      status: "started",
      search: "",
      owner: "",
      page: 1,
    };

    this.initializeInputs();
    this.fetchData();

    this.debouncedSearch = debounce(this.performSearch.bind(this), 300);
    this.rowTemplate = this.element.querySelector('[data-template="row"]');
    this.rowsSection = this.element.querySelector(".rows-section");
  }

  disconnect() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  initializeInputs() {
    if (this.hasStartDateInputTarget) {
      this.startDateInputTarget.value = this.state.startDate;
    }
    if (this.hasEndDateInputTarget) {
      this.endDateInputTarget.value = this.state.endDate;
    }
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = this.state.search;
    }
    if (this.hasStatusSelectTarget) {
      this.statusSelectTarget.value = this.state.status;
    }
  }

  buildFetchUrl() {
    const params = new URLSearchParams();

    if (this.state.startDate) params.set("start_date", this.state.startDate);
    if (this.state.endDate) params.set("end_date", this.state.endDate);
    if (this.state.status) params.set("filter_by", this.state.status);
    if (this.state.search) params.set("search", this.state.search);
    if (this.state.owner) params.set("owner", this.state.owner);

    params.set("page", this.state.page);

    return `${this.urlValue}?${params.toString()}`;
  }

  fetchData() {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    fetch(this.buildFetchUrl(), {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        return response.json();
      })
      .then(this.processData.bind(this))
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }

        this.handleError(error);
      });
  }

  processData(data) {
    if (this.state.page === 1 && this.rowsSection) {
      this.rowsSection.innerHTML = "";
      // Reset selection when loading fresh data (page 1)
      this.state.programIds.clear();
    }

    if (data.programs && data.programs.length > 0) {
      // Auto-select all programs by default
      data.programs.forEach((program) => {
        this.state.programIds.add(program.id.toString());
      });

      this.renderPrograms(data.programs);
      this.hideEmptyState();
    } else if (this.state.page === 1) {
      this.showEmptyState();
    }

    if (this.hasRecordsCountTarget && data.total_count !== undefined) {
      this.recordsCountTarget.textContent = data.records_count_text;
    }

    this.updateSelectedCount();

    // Broadcast update to widgets
    this.broadcastUpdate();
  }

  renderPrograms(programs) {
    if (!this.rowTemplate || !this.rowsSection) return;

    const fragment = document.createDocumentFragment();

    programs.forEach((program) => {
      const row = this.rowTemplate.content.cloneNode(true);
      // TODO: This is *not* a tr, but we'll roll with it
      const tr = row.firstElementChild;

      tr.dataset.programId = program.id;

      const checkbox = tr.querySelector('input[type="checkbox"]');
      if (checkbox) {
        checkbox.value = program.id;
        checkbox.checked = this.state.programIds.has(program.id.toString());
      }

      // TODO: We ought to rely on a [data-attribute] instead, this looks
      // horrendous, but then we gotta patch the BodyCell API as well
      const nameCell = tr.children[1];
      nameCell.textContent = program.name || "";

      const internalNameCell = tr.children[2];
      internalNameCell.textContent = program.internal_name || "";

      const startDateCell = tr.children[3];
      this.renderDateCell(startDateCell, program.start_at);

      const endDateCell = tr.children[4];
      this.renderDateCell(endDateCell, program.end_at);

      fragment.appendChild(row);
    });

    this.rowsSection.appendChild(fragment);
  }

  filterByDate(event) {
    if (event.target === this.startDateInputTarget) {
      this.state.startDate = event.target.value;
    } else if (event.target === this.endDateInputTarget) {
      this.state.endDate = event.target.value;
    }

    this.state.page = 1;
    this.fetchData();
  }

  filterByStatus(event) {
    const value = event.currentTarget.dataset.value || "";
    this.state.status = value;

    this.state.page = 1;
    this.fetchData();
  }

  filterByOwner(event) {
    const value = event.currentTarget.dataset.value || "";
    this.state.owner = value;

    this.state.page = 1;
    this.fetchData();
  }

  resetOwnerFilter() {
    this.state.owner = "";

    this.state.page = 1;
    this.fetchData();
  }

  resetStatusFilter() {
    this.state.status = "all";

    this.state.page = 1;
    this.fetchData();
  }

  searchOnInput(event) {
    this.state.search = event.target.value;
    this.state.page = 1;
    this.debouncedSearch();
  }

  performSearch() {
    this.fetchData();
  }

  clearSearch() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = "";
    }

    this.state.search = "";
    this.state.page = 1;
    this.fetchData();
  }

  toggleProgramSelection(programId) {
    const idString = programId.toString();

    if (this.state.programIds.has(idString)) {
      this.state.programIds.delete(idString);
    } else {
      this.state.programIds.add(idString);
    }

    this.updateSelectedCount();
    this.broadcastUpdate();
  }

  toggleMultipleProgramSelection(programIds) {
    if (!programIds.length) {
      this.state.programIds.clear();
    } else {
      programIds.forEach((i) => {
        if (!this.state.programIds.has(i)) {
          this.state.programIds.add(i.toString())
        }
      })
    }

    setTimeout(() => {
      this.updateSelectedCount();
      this.broadcastUpdate();
    }, 300);
  }

  updateSelectedCount() {
    if (this.hasSelectedCountTarget) {
      const count = this.state.programIds.size;

      if (count == 1) {
        this.selectedCountTarget.textContent =
          this.selectedCountTextsValue.one.replace(/%{count}/g, count);
      } else if (count > 1) {
        this.selectedCountTarget.textContent =
          this.selectedCountTextsValue.other.replace(/%{count}/g, count);
      } else {
        this.selectedCountTarget.textContent = "";
      }
    }
  }

  showEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.remove("hidden");
    }

    if (this.rowsSection) {
      this.rowsSection.innerHTML = "";
    }
  }

  hideEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden");
    }
  }

  handleError(error) {
    this.showEmptyState();
  }

  // Widget Communication
  broadcastUpdate() {
    const eventData = {
      programIds: Array.from(this.state.programIds),
      filters: {
        startDate: this.state.startDate,
        endDate: this.state.endDate,
        status: this.state.status,
        search: this.state.search,
      },
      page: this.state.page,
    };

    document.dispatchEvent(
      new CustomEvent("promote:dashboard:programs-updated", {
        detail: eventData,
      }),
    );
  }

  renderDateCell(cell, dateValue) {
    cell.textContent = "";

    if (dateValue) {
      cell.textContent = dateValue;
    } else {
      const iconContainer = document.createElement("div");
      iconContainer.title = this.individualStartTooltipValue;
      iconContainer.innerHTML = `
        <svg class="w-4 h-4 primary-stroke-slate-800" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" class="primary" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" class="primary" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
      cell.appendChild(iconContainer);
    }
  }
}
